import { useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router';
import { create } from 'zustand';
import type { NavActive } from './WorkspaceNav';

/*
 * The menu's state (docs/navigation-v2-plan.md): where the page in view is (each page says so),
 * whether the menu is hidden and how wide it is (this browser remembers), and the drawer that
 * slides it in over the page on phones and on the board.
 */

const KEY = 'vatman.nav.layout';
export const NAV_WIDTH = { min: 200, max: 420, normal: 264 };

interface NavState {
  /** The organization of the page in view; undefined until a page says (the remembered one then). */
  org: string | null | undefined;
  active: NavActive;
  hidden: boolean;
  width: number;
  drawer: boolean;
}

function load(): Pick<NavState, 'hidden' | 'width'> {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? '{}') as { hidden?: unknown; width?: unknown };
    const width = typeof v.width === 'number' ? Math.min(NAV_WIDTH.max, Math.max(NAV_WIDTH.min, v.width)) : NAV_WIDTH.normal;
    return { hidden: v.hidden === true, width };
  } catch {
    return { hidden: false, width: NAV_WIDTH.normal };
  }
}

function save(s: Pick<NavState, 'hidden' | 'width'>) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ hidden: s.hidden, width: s.width }));
  } catch {
    // Private window or blocked storage: the menu is just shown at the usual width next time.
  }
}

export const useNav = create<NavState>(() => ({ org: undefined, active: { kind: 'none' }, drawer: false, ...load() }));

export function setNavHidden(hidden: boolean) {
  useNav.setState({ hidden });
  save(useNav.getState());
}

export function setNavWidth(width: number, keep = true) {
  useNav.setState({ width: Math.round(Math.min(NAV_WIDTH.max, Math.max(NAV_WIDTH.min, width))) });
  if (keep) save(useNav.getState());
}

export const setNavDrawer = (drawer: boolean) => useNav.setState({ drawer });

/** Where the menu docks beside the page; narrower, it only slides in as a drawer. */
const docked = () => !window.matchMedia('(max-width: 760px)').matches;

/** The "hide menu" button: closes the drawer if the menu is in one, else hides the docked menu. */
export function hideNav() {
  if (useNav.getState().drawer) setNavDrawer(false);
  else setNavHidden(true);
}

/** ⌘\ and the menu buttons: the docked menu hides and comes back; a drawer opens and closes. */
export function toggleNav(canDock = true) {
  const s = useNav.getState();
  if (canDock && docked() && !s.drawer) setNavHidden(!s.hidden);
  else setNavDrawer(!s.drawer);
}

/** A page tells the menu where it is: the row to light up and where "Create" puts new things. */
export function useNavPlace(org: string | null | undefined, active: NavActive) {
  const key = JSON.stringify(active);
  useLayoutEffect(() => {
    useNav.setState({ org, active: JSON.parse(key) as NavActive });
  }, [org, key]);
}

/**
 * The drawer's ways: the page behind it is marked (shell.css shows the menu over it), Escape and
 * a link that took the person elsewhere close it, and so does ⌘\ (`canDock`: the page has a docked
 * menu to hide instead).
 */
export function useNavKeys(canDock: boolean) {
  const drawer = useNav((s) => s.drawer);
  const location = useLocation();
  useEffect(() => setNavDrawer(false), [location.pathname, location.search]);
  useEffect(() => {
    document.body.toggleAttribute('data-nav-open', drawer);
    return () => document.body.removeAttribute('data-nav-open');
  }, [drawer]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && useNav.getState().drawer) setNavDrawer(false);
      // By the key's place, so it works in any keyboard layout.
      if (e.code === 'Backslash' && (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        toggleNav(canDock);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canDock]);
}
